import { useState } from "react";
import Container, { Progress } from "../../common/Container";
import Footer from "../../common/Footer";
import FooterButton from "../../common/FooterButton";
import { Destination, LoadoutGroup, Order, SODestination, ShipTo } from "../../UserFlow";
import { FooterProps } from "./StageOrderTypes";
import SwitchToMobileQRCode from "../../common/SwitchToMobileQRCode";
import useIsAnonymous from "../../common/lib/hooks/useIsAnonymous";
import { useLingui } from "@lingui/react";
import { msg, Plural, Trans } from "@lingui/macro";
import StartOverButton from "../../common/StartOverButton";
import ExpandableText from "../../common/ExpandableText";
import { getCountryByState, getPostalCode, getStateByPostalCode, State } from "../../common/states";
import { KaSelectOption, KaSelect, isMultiValue } from "../../common/KaSelect";
import StateProvincePicker from "../../common/StateProvincePicker";

export interface SelectDestinationProps extends FooterProps {
  allowDestinationChange: boolean;
  allowEnteredShipTo: boolean;
  allowEnteredCityState: boolean;
  requireDestination: boolean;
  destinations: Array<Destination>;
  order?: Order,
  loadoutGroup?: LoadoutGroup,
  initialDestination?: SODestination;
  destinationSelected: (d?: SODestination) => void;
  progress?: Progress;
  startOver: () => void;
}

const SelectCityState = (props: { onDestinationChange: (d: ShipTo | undefined) => void, initialValue?: SODestination }) => {
  const { _ } = useLingui();
  const isAnonymous = useIsAnonymous();
  const [city, setCity] = useState(props.initialValue?.city ?? "");
  const [selectedState, setSelectedState] = useState<State | null>(
    props.initialValue?.state ? getStateByPostalCode(props.initialValue.state) : null
  );

  const updateDestination = (newCity: string, newState: State | null) => {
    setCity(newCity);
    setSelectedState(newState);

    if (newCity && newState) {
      const country = getCountryByState(newState);
      props.onDestinationChange({
        name: `${newCity}, ${getPostalCode(newState)}`,
        city: newCity,
        state: getPostalCode(newState),
        country: country.postalCode,
      });
    } else {
      props.onDestinationChange(undefined);
    }
  };

  return <div className="row">
    <div className="col-md-6">
      <label className="h5 mt-4" htmlFor="city"><Trans>City</Trans></label>
      <input
        id="city"
        className="form-control"
        type="text"
        placeholder={_(msg`Enter city name...`)}
        onChange={ev => { updateDestination(ev.target.value, selectedState) }}
        value={city}
        autoComplete={!isAnonymous ? "on" : "off"}
      />
    </div>
    <div className="col-md-6">
      <label className="h5 mt-4" htmlFor="state-province-picker"><Trans>State/Province</Trans></label>
      <StateProvincePicker
        id="state-province-picker"
        value={selectedState}
        onChange={s => updateDestination(city, s)}
      />
    </div>
  </div>;
}

const SelectDestination = (props: SelectDestinationProps) => {
  const { _ } = useLingui();
  const isAnonymous = useIsAnonymous();
  const [destination, setDestination] = useState(props.initialDestination);
  const OtherOption: KaSelectOption = { value: 'f02e4784-8ab1-4b44-bs7c4-9b91bb2e5637', label: _(msg`Other...`) };

  const destinations = props.destinations.concat(props.order?.customer.destinations ?? []);

  const destOptions: KaSelectOption[] = destinations.map(d => {
    const otherDestinationWithSameName = destinations.some(dto => dto.name === d.name && dto.id !== d.id);

    var destOptionLabel = `${d.name}`;

    if (otherDestinationWithSameName) {
      if (!!d.city && !!d.state) {
        destOptionLabel += ` (${d.city}, ${d.state})`;
      } else if (!!d.city) {
        destOptionLabel += ` (${d.city})`;
      } else if (!!d.state) {
        destOptionLabel += ` (${d.state})`;
      }
    }

    return { value: d.id, label: destOptionLabel };
  });
  if (props.allowEnteredShipTo) { destOptions.push(OtherOption); }

  const initialDropdownValue = (initialDestination?: SODestination): KaSelectOption | null => {
    if (!initialDestination) return null;

    if ("id" in initialDestination) {
      return destOptions.find(o => o.value === initialDestination.id) ?? null;
    }

    return OtherOption;
  }

  const [selectValue, setSelectValue] = useState<KaSelectOption | null>(initialDropdownValue(props.initialDestination));

  const destinationSelected = (opt: KaSelectOption | null) => {
    if (!opt || opt.value === OtherOption.value) {
      setDestination(undefined);
    } else {
      setDestination(destinations.find(d => d.id === opt.value));
    }

    setSelectValue(opt);
  }

  const destinationNameEntered = (value: string) => {
    if (value) {
      setDestination({ name: value });
    } else {
      setDestination(undefined);
    }
  }

  const dropdownAvailable = props.allowDestinationChange && destOptions.length > 0;

  const formWidth = isAnonymous ? "w-65" : "w-75";

  return <Container
    title={_(msg`Load Information`)}
    subtitle={_(msg`Where are you taking the load?`)}
    progress={props.progress} >

    {isAnonymous && <>
      <StartOverButton startOver={props.startOver} />
      <SwitchToMobileQRCode />
    </>}
    <div className={formWidth + " m-auto"}>
      <label className="h2" htmlFor="orderNumber"><Trans>Enter Order Number</Trans></label>
      <input
        type="text"
        id="orderNumber"
        className="form-control form-control-lg"
        style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}
        value={props.order?.number ?? props.loadoutGroup!.name + " (" + props.loadoutGroup?.orderNumbers.join(", ") + ")"}
        disabled />
      {props.order?.releaseNumber && <>
        <label className="h5 mt-4 mb-0"><Trans>Release Number</Trans></label>
        <ExpandableText
          text={props.order.releaseNumber}
          lines={2}
        />
      </>}
      <label className="h5 mt-4 mb-0">
        <Plural
          value={props.order?.products.length ?? props.loadoutGroup!.products.length}
          one="Product"
          other="Products" />
      </label>
      <ExpandableText
        text={props.order?.products.join(", ") ?? props.loadoutGroup!.products.join(", ")}
        lines={2}
        expandTestId="products-expand"
      />
      <label className="h5 mt-4 mb-0">
        <Plural
          value={props.loadoutGroup?.customers.length ?? 1}
          one="Customer"
          other="Customers" />
      </label>
      <ExpandableText
        text={props.order?.customer.name ?? props.loadoutGroup!.customers.join(", ")}
        lines={2}
        expandTestId="customers-expand"
      />
      <label className="h5 mt-4" htmlFor="destination">
        <Plural
          value={props.loadoutGroup?.destinations.length ?? 1}
          one="Destination"
          other="Destinations" />
      </label>
      {props.order && <><KaSelect
        inputId="destination"
        isDisabled={!dropdownAvailable}
        options={destOptions}
        value={selectValue}
        placeholder={dropdownAvailable ? _(msg`Select a destination...`) : _(msg`No destinations available`)}
        onChange={opt => {
          if (isMultiValue(opt)) return;
          destinationSelected(opt ?? null);
        }}
        isSearchable />
        {selectValue?.value === OtherOption.value && props.allowEnteredCityState &&
          <SelectCityState
            onDestinationChange={d => setDestination(d)}
            initialValue={destination} />
        }
        {selectValue?.value === OtherOption.value && !props.allowEnteredCityState &&
          <input
            className="form-control mt-4"
            type="text"
            placeholder={_(msg`Enter destination name...`)}
            onChange={ev => destinationNameEntered(ev.target.value)}
            value={destination?.name}
            autoComplete={!isAnonymous ? "on" : "off"} />
        }
      </>}
      {props.loadoutGroup && <ExpandableText
        text={props.loadoutGroup!.destinations.join(", ")}
        lines={2}
        expandTestId="destinations-expand"
      />}
    </div>
    <Footer
      backButton={<FooterButton {...props.backButton} />}
      nextButton={
        <FooterButton
          disabled={props.requireDestination && !destination && !props.loadoutGroup}
          onClick={() => props.destinationSelected(destination)}>
          <Trans>Next</Trans>
        </FooterButton>
      }
    />
  </Container>
}

export default SelectDestination;
